import React, { useEffect, useRef, useState } from 'react'
import styles from './EventGroupMaintenance.module.css'
import { useParams } from "react-router"
import axios from "axios"
import LoadingBackdrop from "../../components/common/LoadingBackdrop"
import {
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    TextField,
    Typography,
    Card,
    CardHeader,
    Grid,
    IconButton,
    MenuItem,
    Select,
    SelectChangeEvent,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Switch,
    FormControlLabel,
} from "@mui/material"
import { Delete, Edit, ViewModule, ViewQuilt } from "@mui/icons-material"
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { useAutoEvt } from "../../queries/useAutoEvt"

interface Params {
    eventid: string
}

export interface DataItem {
    ticketid: number | string
    ticketname?: string
    groupid?: number | string
    transactionname?: string
}

const SCROLL_ZONE = 100  // px from viewport edge to begin scrolling
const SCROLL_SPEED = 12  // max px per animation frame

const calculateMedianGroupSize = (groups: any[]): number => {
    const sizes = groups
        .filter(g => g.guests?.length > 0)
        .map(g => g.guests.length)
    if (sizes.length === 0) return 8
    sizes.sort((a, b) => a - b)
    const mid = Math.floor(sizes.length / 2)
    return sizes.length % 2 === 0
        ? Math.round((sizes[mid - 1] + sizes[mid]) / 2)
        : sizes[mid]
}

const EventGroupMaintenance = () => {
    const { eventid } = useParams<Params>()
    useAutoEvt(eventid)

    const [dialogObj, setDialogObj] = useState({ msg: "", id: 0 })
    const [dialogMsg, setDialogMsg] = useState("")
    const [groupName, setGroupName] = useState({ name: "", groupid: 0 })
    const [newGroupName, setNewGroupName] = useState("")
    const [loading, setLoading] = useState(false)
    const [ticketItem, setTicketItem] = useState("-1")
    const [groupData, setGroupData] = useState({
        ticketgroups: [],
        ticketitemlist: [],
        selectedticketitemid: 0,
    })
    const [layoutMode, setLayoutMode] = useState<'strict' | 'masonry'>('strict')
    const [groupSize, setGroupSize] = useState(8)
    const [collapseFull, setCollapseFull] = useState(false)
    const [expandedGroups, setExpandedGroups] = useState<Set<number | string>>(new Set())
    const groupSizeInitialized = useRef(false)

    // Guest drag state
    const [draggingItem, setDraggingItem] = useState<DataItem | null>(null)
    const [overGroupId, setOverGroupId] = useState<number | string | null>(null)
    const dragItemRef = useRef<DataItem | null>(null)

    // Group reorder drag state
    const [draggingGroupId, setDraggingGroupId] = useState<number | string | null>(null)
    const draggingGroupIdRef = useRef<number | string | null>(null)
    const [overGroupIdForReorder, setOverGroupIdForReorder] = useState<number | string | null>(null)

    // Auto-scroll refs
    const scrollAnimRef = useRef<number | null>(null)
    const dragClientYRef = useRef<number>(0)

    useEffect(() => {
        getGroups()
    }, [])

    useEffect(() => {
        setTicketItem(groupData.selectedticketitemid.toString())
    }, [groupData.selectedticketitemid])

    const [reloadTrigger, setReloadTrigger] = useState(0)
    const triggerReload = () => setReloadTrigger(n => n + 1)

    useEffect(() => {
        if (reloadTrigger > 0) getGroups()
    }, [reloadTrigger])

    useEffect(() => {
        if (ticketItem !== "-1") getGroups()
    }, [ticketItem])

    const handleSelectionChange = (event: SelectChangeEvent) => {
        setTicketItem(event.target.value as string)
    }

    const getGroups = async () => {
        const body: any = { eventid }
        if (Number(ticketItem) > 0) body.ticketitemid = Number(ticketItem)
        setLoading(true)
        try {
            const { data } = await axios.post('/evtactivitygroups', body)
            const parsedData = JSON.parse(data.jsonString)
            setGroupData(parsedData)
            if (!groupSizeInitialized.current && parsedData.ticketgroups?.length > 0) {
                const median = calculateMedianGroupSize(parsedData.ticketgroups)
                if (median > 0) {
                    setGroupSize(median)
                    groupSizeInitialized.current = true
                }
            }
        } finally {
            setLoading(false)
        }
    }

    const getTicketItemOptions = () =>
        groupData?.ticketitemlist?.map(item => (
            <MenuItem key={item.ticketitemid} value={item.ticketitemid}>
                {item.ticketitemname}
            </MenuItem>
        ))

    const stopAutoScroll = () => {
        if (scrollAnimRef.current !== null) {
            cancelAnimationFrame(scrollAnimRef.current)
            scrollAnimRef.current = null
        }
    }

    const startAutoScroll = () => {
        stopAutoScroll()
        const step = () => {
            const y = dragClientYRef.current
            const viewH = window.innerHeight
            if (y < SCROLL_ZONE) {
                window.scrollBy(0, -SCROLL_SPEED * (1 - y / SCROLL_ZONE))
            } else if (y > viewH - SCROLL_ZONE) {
                window.scrollBy(0, SCROLL_SPEED * ((y - (viewH - SCROLL_ZONE)) / SCROLL_ZONE))
            }
            scrollAnimRef.current = requestAnimationFrame(step)
        }
        scrollAnimRef.current = requestAnimationFrame(step)
    }

    const handleDragStart = (e: React.DragEvent, item: DataItem) => {
        dragItemRef.current = item
        dragClientYRef.current = e.clientY
        e.dataTransfer.effectAllowed = 'move'
        setTimeout(() => setDraggingItem(item), 0)
        startAutoScroll()
    }

    const handleDrag = (e: React.DragEvent) => {
        // clientY is 0 on the final synthetic drag event before drop — ignore it
        if (e.clientY !== 0) dragClientYRef.current = e.clientY
    }

    const handleDragEnd = () => {
        dragItemRef.current = null
        setDraggingItem(null)
        setOverGroupId(null)
        stopAutoScroll()
    }

    const handleGroupHeaderDragStart = (e: React.DragEvent, groupId: number | string) => {
        const group = groupData.ticketgroups.find(g => g.groupid === groupId)
        if (group?.allowchangegroupname === false) return  // default group stays at position 1
        e.stopPropagation()
        e.dataTransfer.setData('text/plain', String(groupId))
        e.dataTransfer.effectAllowed = 'move'
        draggingGroupIdRef.current = groupId
        dragClientYRef.current = e.clientY
        setTimeout(() => setDraggingGroupId(groupId), 0)
        startAutoScroll()
    }

    const handleGroupHeaderDragEnd = () => {
        draggingGroupIdRef.current = null
        setDraggingGroupId(null)
        setOverGroupIdForReorder(null)
        stopAutoScroll()
    }

    const handleGroupDragOver = (e: React.DragEvent, groupId: number | string) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (e.clientY !== 0) dragClientYRef.current = e.clientY
        if (draggingGroupIdRef.current !== null) {
            setOverGroupIdForReorder(groupId)
        } else {
            setOverGroupId(groupId)
        }
    }

    const handleGroupDragLeave = (e: React.DragEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setOverGroupId(null)
            setOverGroupIdForReorder(null)
        }
    }

    const handleGroupDrop = async (e: React.DragEvent, targetGroupId: number | string) => {
        e.preventDefault()
        setOverGroupId(null)
        setOverGroupIdForReorder(null)
        stopAutoScroll()

        if (draggingGroupIdRef.current !== null) {
            const sourceGroupId = draggingGroupIdRef.current
            draggingGroupIdRef.current = null
            setDraggingGroupId(null)

            if (sourceGroupId === targetGroupId) return

            const current = groupData.ticketgroups
            const fromIdx = current.findIndex(g => g.groupid === sourceGroupId)
            const toIdx = current.findIndex(g => g.groupid === targetGroupId)
            if (fromIdx === -1 || toIdx === -1) return

            const reordered = [...current]
            const [moved] = reordered.splice(fromIdx, 1)
            reordered.splice(toIdx, 0, moved)

            // Default group (allowchangegroupname === false) always stays at position 1
            const defIdx = reordered.findIndex(g => g.allowchangegroupname === false)
            if (defIdx > 0) {
                const [def] = reordered.splice(defIdx, 1)
                reordered.unshift(def)
            }

            setGroupData(prev => ({ ...prev, ticketgroups: reordered }))

            setLoading(true)
            try {
                await axios.put('/evtactivitygrouporder', {
                    eventid: Number(eventid),
                    ticketitemid: Number(ticketItem),
                    grouporder: reordered.map(g => Number(g.groupid))
                })
                triggerReload()
            } catch {
                setDialogMsg('Failed to save group order. Please try again.')
                triggerReload()
            } finally {
                setLoading(false)
            }
            return
        }

        const item = dragItemRef.current
        dragItemRef.current = null
        setDraggingItem(null)

        if (!item || item.groupid === targetGroupId) return

        setGroupData(prev => {
            const groups = prev.ticketgroups.map(g => ({
                ...g,
                guests: (g.guests || []).filter(guest => guest.ticketid !== item.ticketid)
            }))
            return {
                ...prev,
                ticketgroups: groups.map(g =>
                    g.groupid === targetGroupId
                        ? { ...g, guests: [...(g.guests || []), { ticketid: item.ticketid, ticketname: item.ticketname, transactionname: item.transactionname }] }
                        : g
                )
            }
        })

        setLoading(true)
        try {
            await axios.put('/evtactivitygroups', {
                eventid,
                ticketitemid: ticketItem,
                groupid: targetGroupId,
                ticketlist: [{ ticketid: item.ticketid }]
            })
            triggerReload()
        } catch {
            setDialogMsg('Failed to move item. Please try again.')
            triggerReload()
        } finally {
            setLoading(false)
        }
    }

    const handleEditGroup = (group) => {
        setNewGroupName(group.groupname)
        setGroupName({ name: group.groupname, groupid: Number(group.groupid) })
    }

    const addNewGroup = () => {
        setNewGroupName('New Group Name')
        setGroupName({ name: 'New Group Name', groupid: -1 })
    }

    const saveGroupName = async () => {
        const finaldata = {
            eventid: Number(eventid),
            groupname: newGroupName,
            groupid: groupName.groupid,
            ticketitemid: Number(ticketItem),
        }
        const { data } = await axios.put('/evtactivitygroupname', finaldata)
        const json = JSON.parse(data.jsonString)
        if (json.status === "failure") {
            setGroupName({ name: '', groupid: 0 })
            setDialogMsg(json.message)
        } else {
            setNewGroupName('')
            setGroupName({ name: '', groupid: 0 })
            triggerReload()
        }
    }

    const handleDeleteGroup = (group) => {
        setDialogObj({
            msg: `Are you sure you want to delete "${group.groupname}"?`,
            id: Number(group.groupid)
        })
    }

    const handleDeleteConfirm = async () => {
        const finaldata = {
            eventid: Number(eventid),
            groupid: dialogObj.id,
            ticketitemid: Number(ticketItem),
            delete: true
        }
        const { data } = await axios.delete('/evtactivitygroupname', { data: finaldata })
        const json = JSON.parse(data.jsonString)
        if (json.status === "failure") {
            setDialogObj({ msg: '', id: 0 })
            setDialogMsg(json.message)
        } else {
            setDialogObj({ msg: '', id: 0 })
            triggerReload()
        }
    }

    const closeDialog = () => {
        setGroupName({ name: '', groupid: 0 })
        setDialogMsg('')
        setDialogObj({ msg: '', id: 0 })
    }

    return (
        <div className={styles.root}>
            <Dialog open={dialogObj.msg.length > 0}>
                <DialogContent>
                    <DialogContentText>{dialogObj.msg}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm}>DELETE</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dialogMsg.length > 0}>
                <DialogContent>
                    <DialogContentText>{dialogMsg}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Ok</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={groupName.name.length > 0} fullWidth>
                <DialogContent>
                    <TextField
                        style={{ marginTop: '10px', width: '100%' }}
                        label="Group Name"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button onClick={saveGroupName}>Save</Button>
                </DialogActions>
            </Dialog>

            <LoadingBackdrop open={loading} />

            <h1>Group Maintenance</h1>

            <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={2} style={{ marginBottom: '20px' }}>
                <Grid item xs={12} sm={3}>
                    <Select
                        onChange={handleSelectionChange}
                        style={{ width: '100%', height: '38px' }}
                        value={ticketItem}
                    >
                        {getTicketItemOptions()}
                    </Select>
                </Grid>
                <Grid item xs={12} sm={6} />
                <Grid item xs={12} sm={3} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" size="small" onClick={addNewGroup} color="primary">
                        Add Group
                    </Button>
                </Grid>
            </Grid>

            {groupData.ticketgroups.length > 1 && (
                <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ display: 'block', marginBottom: '12px' }}
                >
                    Drag the ⠿ handle on a group card to reorder groups. Drag items between groups to reassign them.
                </Typography>
            )}

            <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems={layoutMode === 'strict' ? 'stretch' : 'flex-start'}
                spacing={3}
                style={{ marginTop: '10px' }}
            >
                {groupData.ticketgroups.map(group => {
                    const isOver = overGroupId === group.groupid
                    const isOverForReorder = overGroupIdForReorder === group.groupid
                    const isDraggingFromThisGroup = draggingItem?.groupid === group.groupid
                    const isBeingDragged = draggingGroupId === group.groupid
                    const visibleGuests = group.guests.filter(g => g.ticketid !== draggingItem?.ticketid)
                    const isDefaultGroup = group?.allowchangegroupname === false
                    const isFull = group.guests.length >= groupSize
                    const isCollapsed = collapseFull && isFull && !expandedGroups.has(group.groupid)

                    return (
                        <Grid item xs={12} sm={6} md={4} key={group.groupid}>
                            <Card
                                elevation={isOver || isOverForReorder ? 8 : isBeingDragged ? 1 : 3}
                                onDragEnter={(e) => e.preventDefault()}
                                onDragOver={(e) => handleGroupDragOver(e, group.groupid)}
                                onDragLeave={handleGroupDragLeave}
                                onDrop={(e) => handleGroupDrop(e, group.groupid)}
                                style={{
                                    height: layoutMode === 'strict' ? '100%' : undefined,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    transition: 'box-shadow 0.2s ease, outline 0.2s ease, background-color 0.2s ease, opacity 0.2s ease',
                                    outline: isOverForReorder
                                        ? '2px dashed #9e9e9e'
                                        : isOver
                                            ? '2px solid #1976d2'
                                            : '2px solid transparent',
                                    backgroundColor: isOverForReorder
                                        ? 'rgba(0,0,0,0.03)'
                                        : isOver
                                            ? 'rgba(25, 118, 210, 0.04)'
                                            : undefined,
                                    opacity: isDraggingFromThisGroup || isBeingDragged ? 0.5 : 1,
                                }}
                            >
                                <CardHeader
                                    avatar={
                                        isDefaultGroup ? (
                                            <div style={{ width: '20px' }} />
                                        ) : (
                                            <div
                                                draggable
                                                onDragStart={(e) => handleGroupHeaderDragStart(e, group.groupid)}
                                                onDrag={handleDrag}
                                                onDragEnd={handleGroupHeaderDragEnd}
                                                style={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: '#bdbdbd' }}
                                                title="Drag to reorder"
                                            >
                                                <DragIndicatorIcon style={{ fontSize: '20px' }} />
                                            </div>
                                        )
                                    }
                                    action={
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <IconButton
                                                disabled={group?.allowchangegroupname === false}
                                                onClick={() => handleEditGroup(group)}
                                                size="small"
                                            >
                                                <Edit />
                                            </IconButton>
                                            <IconButton
                                                disabled={group?.allowchangegroupname === false || visibleGuests?.length > 0}
                                                onClick={() => handleDeleteGroup(group)}
                                                size="small"
                                            >
                                                <Delete />
                                            </IconButton>
                                        </div>
                                    }
                                    title={group.groupname}
                                    titleTypographyProps={{ variant: 'h6' }}
                                />

                                <CardContent style={{ flexGrow: 1, paddingTop: 0 }}>
                                    {isCollapsed ? (
                                        <Typography variant="body2" color="textSecondary">
                                            {group.guests.length}/{groupSize} seated.{' '}
                                            <span
                                                style={{ color: '#1976d2', cursor: 'pointer' }}
                                                onClick={() => setExpandedGroups(prev => { const next = new Set(prev); next.add(group.groupid); return next })}
                                            >
                                                Expand
                                            </span>
                                        </Typography>
                                    ) : (
                                        <>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            style={{ marginBottom: '8px', display: 'block' }}
                                        >
                                            {visibleGuests.length} {visibleGuests.length === 1 ? 'item' : 'items'}
                                        </Typography>

                                        <div
                                            style={{
                                                minHeight: '48px',
                                                borderRadius: '6px',
                                                border: isOver && isDraggingFromThisGroup === false
                                                    ? '2px dashed #1976d2'
                                                    : '2px dashed transparent',
                                                padding: '4px',
                                                transition: 'border-color 0.15s ease',
                                            }}
                                        >
                                            {group.guests.map((item) => {
                                                const isDraggingThis = draggingItem?.ticketid === item.ticketid
                                                const showPurchaser = item.transactionname && item.transactionname !== item.ticketname
                                                return (
                                                    <div
                                                        key={item.ticketid}
                                                        draggable
                                                        onDragStart={(e) => handleDragStart(e, { ...item, groupid: group.groupid })}
                                                        onDrag={handleDrag}
                                                        onDragEnd={handleDragEnd}
                                                        style={{
                                                            display: isDraggingThis ? 'none' : 'flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                            padding: '7px 10px',
                                                            marginBottom: '4px',
                                                            borderRadius: '6px',
                                                            backgroundColor: '#f5f5f5',
                                                            border: '1px solid #e0e0e0',
                                                            cursor: 'grab',
                                                            transition: 'opacity 0.15s ease, background-color 0.15s ease',
                                                            userSelect: 'none',
                                                        }}
                                                    >
                                                        <DragIndicatorIcon
                                                            style={{ fontSize: '16px', color: '#9e9e9e', flexShrink: 0 }}
                                                        />
                                                        <Typography variant="body2" style={{ flexGrow: 1 }}>
                                                            {item.ticketname}
                                                        </Typography>
                                                        {showPurchaser && (
                                                            <Typography
                                                                variant="caption"
                                                                style={{
                                                                    color: '#858585',
                                                                    fontSize: '0.7rem',
                                                                    flexShrink: 0,
                                                                    textAlign: 'right',
                                                                }}
                                                            >
                                                                {item.transactionname}
                                                            </Typography>
                                                        )}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    )
                })}
            </Grid>
        </div>
    )
}

export default EventGroupMaintenance
